<template>
  <div
    ref="chatRef"
    class="chat-container"
    :style="{
      '--chat-navigation-height': hasNavigation ? '28px' : '0px',
      '--chat-end-gap': hasNavigation ? '4px' : '12px',
    }"
    @scroll="handleScroll"
  >
    <!-- 顶部哨兵：稳定的零高度锚点，仅供 IntersectionObserver 检测“是否滚到顶部”使用。 -->
    <div ref="topTriggerRef" class="top-sentinel"></div>

    <!-- 加载中 / 全部加载完毕提示：与 ChatHistoryBoundary 互斥渲染，避免边界出现后还占顶部。 -->
    <div v-if="!showHistoryBoundary" class="loading-placeholder">
      <div v-if="isLoading" class="loading-placeholder">
        <span>loading...</span>
      </div>
      <div v-if="!hasMore" class="loading-placeholder">
        <span class="w-500">{{ t('customer.chat.allLoad') }}</span>
      </div>
    </div>

    <!--
      当前 chatId 翻完后展示的视觉边界，必须由用户显式点击才会继续跨会话翻历史。
      :style 注入的 CSS 变量定义本上下文下的“紧凑尺寸预设”。
    -->
    <ChatHistoryBoundary
      v-if="showHistoryBoundary"
      :style="boundaryStyle"
      :color="color"
      @load-history="handleLoadHistory"
    />

    <template v-for="(chat, idx) in currentChatBox" :key="chat.sequenceId">
      <!--
        公告内联块：渲染在“首个属于当前会话的消息”之前，即历史消息与当前会话消息的边界处。
        currentChatBox 为空或全为历史时由下方兜底 v-if 处理。
      -->
      <div
        v-if="idx === announcementInsertIndex"
        class="inline-announcement"
        role="region"
        :aria-label="t('session.announcement.title')"
      >
        <h2 class="inline-announcement-title">{{ t('session.announcement.title') }}</h2>
        <div class="inline-announcement-content">
          <AutoLinkText :text="localizedAnnouncement" />
        </div>
      </div>
      <CustomerContent
        :username="chat.username"
        :sender="chat.sender"
        :avatar="chat.avatar"
        :text="chat.content"
        :queue="chat.queuePosition"
        :type="chat.contentType"
        :timestamp="chat.timestamp"
        :show-time="chat.showTime"
        :is-first="chat.isFirst"
        :is-agent="chat.isAgent"
        :is-me="chat.isMe"
        :bubble-color="color"
        :duration="chat.duration"
        :fileSize="chat.fileSize"
        :file-name="chat.fileName"
        :is-warn="chat.isWarn"
        :is-blocked="chat.isBlocked"
        :message-id="chat.messageId"
        :chat-id="chat.chatId"
      />
    </template>
    <!-- 兜底：空列表或全为历史时，插入点索引会等于 length，v-for 内的 v-if 无法命中 -->
    <div
      v-if="shouldShowAnnouncement && announcementInsertIndex === currentChatBox.length"
      class="inline-announcement"
      role="region"
      :aria-label="t('session.announcement.title')"
    >
      <h2 class="inline-announcement-title">{{ t('session.announcement.title') }}</h2>
      <div class="inline-announcement-content">
        <AutoLinkText :text="localizedAnnouncement" />
      </div>
    </div>
  </div>
</template>

<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script lang="ts">
/**
 * CustomerContainer - 访客端主聊天区的消息滚动容器。
 * Purpose: 展示当前会话消息列表，负责首屏自动补齐、用户上翻加载更多、滚动位置保持，
 *          以及“当前会话翻完后由用户点击 ChatHistoryBoundary 才跨会话翻历史”。
 * State: chatRef/topTriggerRef（容器与顶部触发点）、isLoading/hasMore（加载状态）、
 *        isPinnedToBottom（贴底粘附）、unlockedHistory（用户是否已确认查看更早会话历史）。
 * Props: color（气泡/品牌色）、hasNavigation（是否有底部导航；导航已有 8px 上间距，
 *        因此消息列表末端留白在 4px / 12px 间切换，保持总间距为 12px）。
 */
import { computed, defineComponent, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type { PropType } from 'vue'
import CustomerContent from './CustomerContent.vue'
import ChatHistoryBoundary from './ChatHistoryBoundary.vue'
import AutoLinkText from './AutoLinkText.vue'
import { useChatStore } from '../../../store/chat'
import { getChatHistory } from '../../../api/simple-api'
import { getChannelId } from '../../../utils/channelStorage'
import { useI18n } from '../../../i18n/simple-i18n'
import { useLang } from '../../../hooks/useLang'

type AnnouncementLocale = 'zh-CN' | 'zh-TW' | 'en-US'

export default defineComponent({
  name: 'CustomerContainer',
  components: { CustomerContent, ChatHistoryBoundary, AutoLinkText },
  props: {
    color: {
      type: String,
      default: '#2e9b4a',
    },
    hasNavigation: {
      type: Boolean,
      default: true,
    },
    announcementFlag: {
      type: Boolean,
      default: false,
    },
    announcement: {
      type: Object as PropType<Partial<Record<AnnouncementLocale, string>>>,
      default: () => ({}),
    },
  },
  setup(props) {
    const cStore = useChatStore()
    const { t } = useI18n()
    const { getUCLang } = useLang()

    const chatRef = ref<HTMLElement>()
    const topTriggerRef = ref<HTMLElement>()
    const observer = ref<IntersectionObserver | null>(null)
    const isLoading = ref<boolean>(false)
    const hasMore = ref<boolean>(true)
    // 贴底粘附标记：true 表示用户停留在底部，允许自动贴底（挂载兜底 / 新消息到达）；
    // 用户上滑离开底部后由 handleScroll 置为 false，抑制一切自动贴底，避免覆盖正在进行的历史阅读。
    const isPinnedToBottom = ref<boolean>(true)
    const scrollAnimationFrameId = ref<number | null>(null)
    const pendingTimers = new Set<ReturnType<typeof setTimeout>>()
    // 用户是否已显式点击“查看更早会话历史”按钮：未点击时阻断 loadMore 跨会话翻历史。
    const unlockedHistory = ref<boolean>(false)

    const TIME_GAP_MINUTES = 5
    const SCROLL_TOP_THRESHOLD = 50
    // 距底 <= 该阈值即视为“用户仍贴在底部”，用于更新 isPinnedToBottom。
    const STICK_TO_BOTTOM_THRESHOLD = 50
    // 单次 loadMore 最多续翻次数：覆盖“消息数恰好为 20 倍数”导致的连续空响应，并为异常数据保留硬上限。
    const MAX_LOAD_MORE_ITERATIONS = 5

    // ChatHistoryBoundary 在容器内的紧凑尺寸预设。
    const boundaryStyle: Record<string, string> = {
      '--boundary-max-width': '236px',
      '--boundary-padding': '12px 0',
      '--boundary-gap': '8px',
      '--boundary-button-font-size': '12px',
      '--boundary-button-line-height': '20px',
      '--boundary-divider-height': '14px',
      '--boundary-divider-text-font-size': '10px',
      '--boundary-divider-text-line-height': '14px',
    }

    const setTrackedTimeout = (fn: () => void, wait: number) => {
      const id = setTimeout(() => {
        pendingTimers.delete(id)
        fn()
      }, wait)
      pendingTimers.add(id)
      return id
    }

    // ===== 门控 computed（基于 store 顶层状态）=====

    // 当前 chatId 是否已翻完：updateLastChatAndSequence 在拿到 <20 条后会把游标切到更早历史 chatId，
    // 此时 lastChatId 不再等于当前会话 chatId。
    const isCurrentChatExhausted = computed(() => cStore.lastChatId !== cStore.chatId)

    // 新访客：游标已到终态且无更早历史会话（chatHistory 为空）。
    const isFreshNewCustomer = computed(
      () =>
        cStore.lastChatId === '0' &&
        cStore.lastSequenceId === '0' &&
        cStore.chatHistory.length === 0,
    )

    // 自动补齐门控：当前 chatId 翻完且未解锁时，禁止 checkAndLoadIfNeeded 的“无滚动条自动补齐”。
    const isHistoryGated = computed(() => isCurrentChatExhausted.value && !unlockedHistory.value)

    // 边界点击门控：老访客当前 chatId 翻完、仍有更早历史会话、且未解锁时为 true（新访客被排除）。
    const isBoundaryPending = computed(
      () => isCurrentChatExhausted.value && !unlockedHistory.value && !isFreshNewCustomer.value,
    )

    // 是否在顶部展示 ChatHistoryBoundary。
    const showHistoryBoundary = computed(() => hasMore.value && isBoundaryPending.value)

    // ===== 内联公告 =====

    // 当前语言下的公告正文。缺省语种或空串时按“无公告”处理。
    const localizedAnnouncement = computed(() => props.announcement[getUCLang()] ?? '')

    // 只有 flag 打开且当前语种有实际正文时才展示；
    // 且列表中一旦出现非当前 chatId 的消息（即已加载/展示了更早会话历史），公告直接隐藏。
    const shouldShowAnnouncement = computed(() => {
      if (!props.announcementFlag || !localizedAnnouncement.value) return false
      const currentId = cStore.chatId
      return !currentChatBox.value.some((it) => it.chatId !== currentId)
    })

    // 公告插入点：currentChatBox 中"最末尾同 chatId 连续组"的起点下标。
    // 语义：末尾这一组即"当前活跃会话"的消息，公告放在这组之前，之上是历史消息。
    // 用最末消息的 chatId 而不是 cStore.chatId 判定的原因：
    //   重新会话时服务端先下 RC/CI 更新 state.chatId=B，但 chatInfo 里还全是旧 chatId=A 的消息，
    //   若按 cStore.chatId 判定，findIndex 返回 -1、公告会被兜底渲染到末尾；等第一条 B 消息真正
    //   到达时又被挤到 B 消息上方，视觉上出现"公告卡在新旧消息之间"的错乱。
    //   以最末消息 chatId 为准则可让公告始终贴在"最新一组"之上，跨会话切换时无中间态。
    // 空列表：返回 0，让下方兜底 v-if 命中并在顶部渲染。
    const announcementInsertIndex = computed(() => {
      if (!shouldShowAnnouncement.value) return -1
      const items = currentChatBox.value
      if (items.length === 0) return 0
      const tailChatId = items[items.length - 1].chatId
      let i = items.length - 1
      while (i > 0 && items[i - 1].chatId === tailChatId) i--
      return i
    })

    // ===== 消息列表派生 =====

    const currentChatBox = computed(() => {
      const chats = cStore.chatInfo

      // 单次遍历去重：仅对“有真实 sequenceId”的消息去重（历史拼接可能产生重复）；
      // 本地发送的消息 sequenceId 为 'unknown'，秒级时间戳易碰撞，故一律保留，避免快速连发被误删。
      const seen = new Set<string>()
      const filtered: typeof chats = []
      for (const item of chats) {
        if (item.sequenceId && item.sequenceId !== 'unknown') {
          const key = `${item.sequenceId}-${item.timestamp}`
          if (seen.has(key)) continue
          seen.add(key)
        }
        filtered.push(item)
      }

      return filtered.map((chat, index) => {
        const previousChat = index > 0 ? filtered[index - 1] : null

        // 时间差（分钟），首条设为无穷大；仅用于 isFirst 视觉分组。
        const timeDiffMinutes = previousChat
          ? (new Date(chat.timestamp).getTime() - new Date(previousChat.timestamp).getTime()) /
            (1000 * 60)
          : Infinity

        // showTime（日期分隔条）：跨天才显示。直接比较 "YYYY-MM-DD" 日期段字符串，
        // 避免 new Date() 对非标准格式的时区解析歧义。
        const isNewDay =
          !previousChat || chat.timestamp.split(' ')[0] !== previousChat.timestamp.split(' ')[0]

        return {
          ...chat,
          isFirst:
            !previousChat ||
            chat.senderId !== previousChat.senderId ||
            timeDiffMinutes > TIME_GAP_MINUTES,
          isAgent: cStore.currentAgent === chat.senderId,
          showTime: isNewDay,
          // 视角化 isMe：
          // - 访客视角：sender==='custom' 即"我"
          // - 客服视角：sender==='agent' 且 senderId === 当前客服 agentId（URL 里带下来的 cStore.currentAgent）
          //   历史里可能包含其他客服（转接前的接待客服/协助客服）发的消息，靠 senderId 精确区分。
          isMe:
            cStore.role === 'agent'
              ? chat.sender === 'agent' &&
                String(chat.senderId ?? '') === String(cStore.currentAgent ?? '')
              : chat.sender === 'custom',
        }
      })
    })

    // ===== 滚动 =====

    // 通用滚动函数：to=number 时滚到指定位置；to='bottom' 时滚到底部。
    const scrollChatTo = (to: number | 'bottom') => {
      const scrollContainer = chatRef.value
      if (!scrollContainer) return
      if (to === 'bottom') {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'instant' as ScrollBehavior,
        })
      } else {
        scrollContainer.scrollTo({ top: to, behavior: 'auto' })
      }
    }

    const scrollToBottom = () => scrollChatTo('bottom')
    const scrollTo = (height: number) => scrollChatTo(height)

    /**
     * 前沿节流（leading + 冷却期丢弃，无尾部补发）。
     * 必须用前沿而非尾部防抖：触顶信号会由持续滚动和 iOS 本地回弹反复产生，
     * 尾部防抖每次都被重排，实测把触顶加载推迟 0.5~3.2s（重排最多 126 次）。
     * @param func 目标函数
     * @param wait 冷却期毫秒数，期间的重复信号直接丢弃
     */
    const leadingThrottle = <T extends (...args: any[]) => any>(func: T, wait: number) => {
      let cooldown: ReturnType<typeof setTimeout> | null = null
      const throttled = (...args: Parameters<T>) => {
        if (cooldown) return
        cooldown = setTimeout(() => {
          cooldown = null
        }, wait)
        func(...args)
      }
      throttled.cancel = () => {
        if (cooldown) {
          clearTimeout(cooldown)
          cooldown = null
        }
      }
      return throttled
    }

    // 自动触发的加载更多（触顶滚动 / IntersectionObserver / 首屏补齐）：
    // 按 isBoundaryPending 阻断“老访客跨会话翻历史”（强制走边界点击）；新访客放行。
    // 用户显式点击边界按钮不走这里，避免被冷却期丢弃，见 handleLoadHistory。
    const throttledLoadMore = leadingThrottle(async () => {
      if (isLoading.value || !hasMore.value) return
      if (isBoundaryPending.value) {
        console.log('老访客当前 chatId 已翻完，等待用户点击“查看历史消息”再继续')
        return
      }
      await loadMore()
    }, 300)

    // 滚动事件处理（rAF 节流）
    const handleScroll = () => {
      if (scrollAnimationFrameId.value) {
        cancelAnimationFrame(scrollAnimationFrameId.value)
      }
      scrollAnimationFrameId.value = requestAnimationFrame(() => {
        const container = chatRef.value
        if (!container) return

        // 贴底粘附判定（先于翻页守卫）：距底 <= 阈值视为仍贴底；上滑离开则抑制自动贴底。
        const distanceFromBottom =
          container.scrollHeight - container.clientHeight - container.scrollTop
        isPinnedToBottom.value = distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD

        if (isLoading.value || !hasMore.value) return
        if (container.scrollTop <= SCROLL_TOP_THRESHOLD) {
          console.log('滚动到顶部，触发加载')
          throttledLoadMore()
        }
      })
    }

    /**
     * 触摸守卫：容器没有可滚内容时，拦下单指手势，不让它沿滚动链传播到文档。
     * 否则浏览器的文档级手势（移动端下拉重载 / 边界回弹）会接管并锁定整个手势，
     * 表现为“历史消息到达前上滑，列表卡死直到松手”。
     * overscroll-behavior 对没有 scrollport 的元素无效，因此必须在这里拦。
     * 每次现场读布局而不缓存“是否可滚”，容器一旦有内容立即原样放行。
     * 必须 non-passive：只有可取消的 touchmove 才能 preventDefault。
     */
    const handleTouchMoveGuard = (e: TouchEvent) => {
      const container = chatRef.value
      if (!container) return
      // 多指手势（缩放）放行，避免影响无障碍缩放
      if (e.touches.length > 1) return
      if (container.scrollHeight > container.clientHeight) return
      if (!e.cancelable) return
      e.preventDefault()
    }

    // 智能补齐：首屏不足以撑出滚动条时自动续翻；isHistoryGated 时停止“无滚动条自动补齐”。
    const checkAndLoadIfNeeded = () => {
      const container = chatRef.value
      if (!hasMore.value || isLoading.value || !container) return false

      if (isHistoryGated.value) {
        console.log('当前 chatId 已翻完，停止首屏补齐')
        return false
      }

      const hasScroll = container.scrollHeight > container.clientHeight
      if (!hasScroll) {
        console.log('内容不足，加载更多')
        throttledLoadMore()
        return true
      }
      return false
    }

    // 初始化观察器（作为滚动触顶的备用触发方案）
    const initObserver = () => {
      if (observer.value) {
        observer.value.disconnect()
      }

      observer.value = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && hasMore.value && !isLoading.value) {
              console.log('IntersectionObserver 触发加载')
              throttledLoadMore()
            }
          })
        },
        {
          root: chatRef.value,
          rootMargin: '100px 0px 0px 0px',
          threshold: 0.01,
        },
      )

      if (topTriggerRef.value && observer.value) {
        observer.value.observe(topTriggerRef.value)
      }
    }

    /**
     * 拉取更早消息并拼接到列表顶部。
     * - loop-on-empty：吸收“消息恰好 20 倍数”产生的空响应；
     * - 跨会话门控：游标切到更早 chatId 且未解锁时中止 loop，由 ChatHistoryBoundary 暴露按钮等待点击；
     * - 终态处理：无更早会话(新访客)时仅在“有滚动条”才翻 hasMore；有更早会话翻完则立即翻 hasMore=false。
     */
    const loadMore = async () => {
      if (isLoading.value || !hasMore.value) return

      // 游标已终态：是否显示“已加载完”取决于能否真正上翻到顶。
      if (cStore.lastChatId === '0' && cStore.lastSequenceId === '0') {
        console.log('全部会话加载完毕')
        const container = chatRef.value
        const hasScroll = !!container && container.scrollHeight > container.clientHeight
        if (hasScroll) hasMore.value = false
        return
      }

      isLoading.value = true
      const container = chatRef.value
      const oldScrollHeight = container?.scrollHeight ?? 0
      const oldScrollTop = container?.scrollTop ?? 0

      try {
        let appended = false
        for (let i = 0; i < MAX_LOAD_MORE_ITERATIONS; i++) {
          if (cStore.lastChatId === '0' && cStore.lastSequenceId === '0') {
            hasMore.value = false
            break
          }

          // 游标即将跨会话且用户未解锁 → 中止 loop，等待用户点击边界按钮。
          if (cStore.lastChatId !== cStore.chatId && !unlockedHistory.value) {
            console.log('游标即将跨会话，等待用户显式点击，loop 中止')
            break
          }

          const res = await getChatHistory(cStore.lastChatId, cStore.lastSequenceId, getChannelId())
          const history = await res.json()
          const data = history.data ?? []

          if (data.length > 0) {
            cStore.loadOldChat(data)
            cStore.updateLastChatAndSequence(data)
            appended = true
            // 游标切到终态：无更早历史会话(新访客)不翻 hasMore；有更早历史会话翻完则翻 hasMore=false。
            if (cStore.lastChatId === '0' && cStore.lastSequenceId === '0') {
              if (cStore.chatHistory.length > 0) {
                hasMore.value = false
              }
            }
            break
          }

          // 空响应：仅推进游标继续下一轮，不释放 isLoading，让“空上翻”在体感上被吸收。
          cStore.updateLastChatAndSequence([])
        }

        await nextTick()

        // 仅在真正拼接到新消息时恢复滚动位置；空响应轮次不应触发视觉变化。
        if (container && appended) {
          const addedHeight = container.scrollHeight - oldScrollHeight
          scrollTo(oldScrollTop <= SCROLL_TOP_THRESHOLD ? addedHeight : oldScrollTop + addedHeight)
        }

        // 首屏补齐：首页不足以撑满容器时自动续翻。
        if (hasMore.value) {
          setTrackedTimeout(checkAndLoadIfNeeded, 100)
        }
      } catch (error) {
        console.error(error)
      } finally {
        isLoading.value = false
      }
    }

    // 用户点击 ChatHistoryBoundary：解锁跨会话翻历史并立即拉取更早会话第一页。
    // 绕开 throttledLoadMore：显式点击是独立意图，不能被自动触发留下的冷却期丢弃。
    const handleLoadHistory = () => {
      if (isLoading.value || !hasMore.value) return
      unlockedHistory.value = true
      loadMore()
    }

    // ===== watch =====

    // 内容变化自动贴底：仅在用户仍贴底时执行，避免把正在看历史的用户拽回底部。
    watch(
      () => currentChatBox.value,
      () => {
        if (isLoading.value) return
        if (!isPinnedToBottom.value) return
        setTrackedTimeout(scrollToBottom, 100)
      },
    )

    // “自己发送消息”的贴底信号：无视当前粘附态强制贴底并重新进入粘附。
    watch(
      () => cStore.scrollToBottomSignal,
      () => {
        isPinnedToBottom.value = true
        setTrackedTimeout(scrollToBottom, 100)
      },
    )

    onMounted(() => {
      chatRef.value?.addEventListener('touchmove', handleTouchMoveGuard, { passive: false })
      setTrackedTimeout(initObserver, 100)
      // 1000ms 兜底：等待图片等异步内容撑开高度后再贴底一次，仅在仍贴底时执行。
      setTrackedTimeout(() => {
        if (isPinnedToBottom.value) scrollToBottom()
      }, 1000)

      nextTick(() => {
        scrollToBottom()
        // 首屏补齐检查：首批消息不足以撑满聊天区时自动续翻。
        setTrackedTimeout(checkAndLoadIfNeeded, 200)
      })
    })

    onUnmounted(() => {
      chatRef.value?.removeEventListener('touchmove', handleTouchMoveGuard)
      if (scrollAnimationFrameId.value) {
        cancelAnimationFrame(scrollAnimationFrameId.value)
      }
      observer.value?.disconnect()
      observer.value = null
      throttledLoadMore.cancel()
      pendingTimers.forEach((id) => clearTimeout(id))
      pendingTimers.clear()
    })

    return {
      t,
      currentChatBox,
      chatRef,
      topTriggerRef,
      isLoading,
      hasMore,
      handleScroll,
      showHistoryBoundary,
      handleLoadHistory,
      boundaryStyle,
      localizedAnnouncement,
      shouldShowAnnouncement,
      announcementInsertIndex,
    }
  },
})
</script>

<style lang="less" scoped>
.chat-container {
  position: relative;
  // display: grid;
  // grid-template-rows: 1fr auto;
  box-sizing: border-box;
  padding-inline: 16px;
  padding-bottom: var(--chat-end-gap);
  height: stretch;
  overflow-y: auto;
  overflow-x: hidden;
  // 阻断滚动链：到达上下边界后不把滚动量传给祖先/宿主页。
  // 否则浏览器会把整个滚轮手势锁定到祖先滚动器上，手势内反向滚动也不会回到本容器，
  // 表现为“到底后先下滚再立刻上滚，列表被卡住直到停手”。
  overscroll-behavior: contain;
  scroll-padding-bottom: var(--chat-end-gap);

  scrollbar-width: thin; /* Firefox支持 */
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent; /* Firefox: 滑块色 + 轨道透明 */

  &::-webkit-scrollbar {
    width: 4px;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border: none;
    box-shadow: none;
  }

  &::-webkit-scrollbar-track-piece {
    background: transparent;
  }

  &::-webkit-scrollbar-corner {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}

.top-sentinel {
  width: 100%;
  height: 0;
  min-height: 0;
}

.loading-placeholder {
  display: flex;
  height: 54px;
  min-height: 54px;
  justify-content: center;
  align-items: center;
}

.w-500 {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.45);
}

/* 内联公告：随消息流出现在历史/当前会话分界处 */
.inline-announcement {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 12px 0;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-default, #edefed);
  background: var(--white, #fff);
}

.inline-announcement-title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: var(--text-primary, #202221);
  text-align: center;
}

.inline-announcement-content {
  color: var(--text-primary, #202221);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  text-align: left;
}
</style>
